<template>
  <div id="month">
    <div v-for="{ date, key } in weekdays" class="day" :key="key">
      <div class="header py-1 px-2 text-body-2 flex-grow-0" v-if="key < 5" :class="$utils.datePPF(date)">
        {{ $moment(date).format('ddd') }}
      </div>
      <div class="d-flex align-center py-1 px-2 text-body-2 bg-secondary flex-grow-0">
        <div :class="$utils.datePPF(date)">
          {{ $moment(date).format('Do') }}
        </div>
        <v-spacer></v-spacer>
        <div>
          <v-chip size="small" class="px-2 coloured ruleGrey text-black mx-1">{{ muTotalOfDay(date) }} mµ</v-chip>
          <v-chip size="small" class="px-2 coloured cut mx-1">{{ getCutMuCapacityPercentage(date) }}%</v-chip>
          <v-chip size="small" class="px-2 coloured workshop mx-1">{{ getWorkshopMuCapacityPercentage(date) }}%</v-chip>
        </div>
      </div>
      <draggable
        v-model="tasksByDay[date]"
        :group="{ name: 'scheduled-tasks', pull: true, put: ['scheduled-tasks', 'unscheduled-tasks'] }"
        @end="onSortEnd"
        @add="onSortEnd"
        class="draggable-list flex-grow-1 scheduled-tasks"
        :animation="0"
        :id="date"
        item-key="id"
      >
        <template #item="{ element: task }">
          <div
            class="task text-body-2"
            :class="[
              task.type,
              $utils.camelize(task.subtype),
              { coloured: !task.complete && !task.paused },
              { paused: task.paused },
              {
                ghost: focussedTask !== null && task.shipmentID !== focussedTask.shipmentID
              },
              {
                focussed: focussedTask !== null && task.shipmentID == focussedTask.shipmentID
              }
            ]"
            :id="task.id"
          >
            <v-row>
              <v-col cols="12" class="d-flex align-center">
                <v-icon
                  v-if="task.type === 'cut' && !task.toolpathed && !task.repeatTaskId"
                  :size="14"
                  :style="{
                    'padding-bottom': '1px',
                    color: task.complete ? 'black' : 'white',
                    'margin-right': '2px'
                  }"
                  >mdi-alert</v-icon
                >
                <v-icon
                  v-if="
                    task.type === 'dispatch' &&
                    task.subtype === 'To customer' &&
                    task.courierBooked &&
                    !task.complete &&
                    !task.repeatTaskId
                  "
                  :size="15"
                  :color="isShipmentPropertyTrue(task.shipmentID, 'balanceOutstanding') ? 'orange' : 'white'"
                  >mdi-truck-check</v-icon
                >
                <div class="mr-2" @click="$emit('focusTask', task)">
                  {{ task.orderNumber || task.invoiceNumber }}
                </div>
                <div class="flex-grow-1 d-flex" @click="$emit('openTask', task)">
                  <div>
                    {{ task.objectType }}
                    {{ task.subtype ? task.subtype : task.type }}
                  </div>
                  <v-spacer />
                  <v-spacer />
                  <div>
                    {{ task.units }}
                  </div>
                  <div v-if="['cut'].includes(task.type) && task.muCut">
                    <span class="mx-1">/</span>{{ $utils.toFixedNumber(task.muCut, 1) }} mµ
                  </div>
                  <div v-else-if="['finish', 'assemble'].includes(task.type) && task.muFinish">
                    <span class="mx-1">/</span>{{ $utils.toFixedNumber(task.muFinish, 1) }} mµ
                  </div>
                  <div v-else-if="['pack'].includes(task.type) && task.muPack">
                    <span class="mx-1">/</span>{{ $utils.toFixedNumber(task.muPack, 1) }} mµ
                  </div>
                  <div v-else-if="['dispatch'].includes(task.type) && task.muDispatch">
                    <span class="mx-1">/</span>{{ $utils.toFixedNumber(task.muDispatch, 1) }} mµ
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </template>
      </draggable>
    </div>
  </div>
</template>
<script>
import draggable from 'vuedraggable'

export default {
  name: 'Month',
  components: { draggable },
  props: ['tasksByDay', 'capacityObject', 'focussedTask', 'staffUnitsPerDay', 'isShipmentPropertyTrue'],
  computed: {
    weekdays() {
      return Object.keys(this.tasksByDay)
        .map((date, key) => ({ date, key }))
        .filter(({ date }) => this.$moment(date).weekday() < 5)
    }
  },
  methods: {
    getDailyRequiredCapacity(date) {
      let count = 0

      for (var i = 0; i < this.tasksByDay[date].length; i++) {
        // should we exclude this from capacity?
        var t = this.tasksByDay[date][i]
        if (t.type != 'dispatch') {
          count += parseInt(this.tasksByDay[date][i].units)
        }
      }
      return count
    },
    capacityForDay(day) {
      if (day) {
        let hours = 0
        let workers = day.working.concat(day.freelancers).concat(day.overtime)
        for (const worker of workers) {
          hours += worker.hours
        }
        return hours * (this.staffUnitsPerDay / 8)
      }
    },
    muTotalOfDay(date) {
      let muTotal = 0
      for (const task of this.tasksByDay[date]) {
        switch (task.type) {
          case 'cut':
            muTotal += task?.muCut || 0
            break
          case 'finish':
          case 'assemble':
            muTotal += task?.muFinish || 0
            break
          case 'pack':
            muTotal += task?.muPack || 0
            break
          case 'dispatch':
            muTotal += task?.muDispatch || 0
            break
          default:
            break
        }
      }
      return Number(muTotal.toFixed(1))
    },
    getWorkshopMuRequirement(date) {
      let workshopMuTotal = 0

      for (const task of this.tasksByDay[date]) {
        switch (task.type) {
          case 'finish':
          case 'assemble':
            workshopMuTotal += task?.muFinish || 0
            break
          case 'pack':
            workshopMuTotal += task?.muPack || 0
            break
          case 'dispatch':
            workshopMuTotal += task?.muDispatch || 0
            break
          default:
            break
        }
      }

      return Number(workshopMuTotal.toFixed(1))
    },
    workshopMuCapacityForDay(day) {
      if (day) {
        let hours = 0
        let workers = day?.working.concat(day?.freelancers).concat(day?.overtime)
        for (const worker of workers) {
          hours += worker.hours
        }
        hours -= day?.cutting // remove cutting hours (only)
        return hours * (this.$store.state.mu.workshopCapacity / 8) // 8 hour day
      }
    },
    getCutMuCapacityPercentage(date) {
      const muCut = this.tasksByDay[date].reduce((sum, val) => {
        return val.type === 'cut' && val?.muCut ? sum + parseInt(val?.muCut) : sum
      }, 0)
      return Math.round((muCut / this.$store.state.mu.cutCapacity) * 100)
    },
    getWorkshopMuCapacityPercentage(date) {
      const capacity = this.workshopMuCapacityForDay(this.capacityObject[date])
      const mu = this.getWorkshopMuRequirement(date)
      const workshopMuPercentage = capacity && mu ? Math.round((mu / capacity) * 100) : 0

      return workshopMuPercentage
    },
    onSortEnd(e) {
      this.$emit('onSortEnd', e)
    }
  }
}
</script>
<style lang="scss">
#month {
  position: absolute;
  overflow-y: scroll;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  height: 100%;
  transition: width 0.5s;
  .day {
    width: 20%;
    border-right: solid 1px $ruleGrey;
    border-bottom: solid 1px $ruleGrey;
    display: flex;
    flex-direction: column;

    .draggable-list {
      min-height: 36px;
    }

    &:last-child,
    &.no-border {
      border: none;
    }
    .task-type {
      background: $sand;
      border-top: solid 1px $ruleGrey;
      display: flex;
      justify-content: flex-end;
      padding: 4px;
    }
  }
}
.staff-card {
  display: flex;
  flex-direction: column;
  background: white;
  padding: 4px 6px;
  cursor: pointer;
  .worker {
    display: flex;
    justify-content: space-between;
    padding: 2px 0px;
  }
}
</style>
